import React, { useState } from 'react'

import { ETaskStatus, TSubTask } from 'types/GlobalTask'
import { SxProps } from '@mui/material'

import { useTaskActions } from 'redux/actions/Tasks'
import { useTranslation } from 'language/translator'

import Box from 'components_new/atoms/Box'
import Chip from 'components_new/atoms/Chip'
import Icon from 'components_new/atoms/Icon'
import LinearProgress from 'components_new/atoms/LinearProgress'

import GenericListItem from 'components_new/molecules/GenericListItem'
import IconButton from 'components_new/molecules/IconButton'
import OptionsMenuButton from 'components_new/molecules/OptionsMenuButton'
import SectionHeader from 'components_new/molecules/SectionHeader'
import SimpleTable from 'components_new/molecules/SimpleTable'

import SelectUser from 'components_new/organisms/SelectUser'
import TaskStatusChip from 'components_new/organisms/tasks/TaskStatusChip'
import DeleteDialog from 'components_new/organisms/dialogs/DeleteDialog'
import EditSubtaskDialog from 'components_new/organisms/dialogs/EditSubtaskDialog'

import AddSubtaskItem from './AddSubtaskItem'
import DeadlineField from './DeadlineField'

interface SubtasksTableProps {
  items: TSubTask[]
  parentId: string
  sx?: SxProps
}

const SubtasksTable = (props: SubtasksTableProps) => {
  const { items, parentId, sx } = props

  const { t } = useTranslation()
  const { tryDeleteTask, tryUpdateTask } = useTaskActions()

  const [addSubtask, setAddSubtask] = useState<boolean>(false)
  const [deleteItem, setDeleteItem] = useState<TSubTask | null>(null)
  const [editItem, setEditItem] = useState<TSubTask | null>(null)

  const nItems = items.length
  const nDoneItems = items.filter(
    (item) => item.status === ETaskStatus.DONE
  ).length

  const handleUpdateUser = (id: string | undefined, value: string | null) => {
    if (!id) return

    tryUpdateTask(id, {
      responsible_account_id: value
    })
  }

  const handleUpdateDeadline = (id: string, value: string | null) => {
    tryUpdateTask(id, {
      deadline_date: value
    })
  }

  return (
    <>
      <Box sx={sx}>
        {items.length === 0 ? (
          <Chip
            icon={<Icon name="Add" />}
            label={t('tasks', 'add_subtask')}
            onClick={() => setAddSubtask(true)}
            size="small"
          />
        ) : (
          <>
            <SectionHeader
              actions={
                <IconButton
                  name="Add"
                  onClick={() => setAddSubtask(true)}
                  size="small"
                  title={t('tasks', 'add_subtask')}
                  variant="outlined"
                />
              }
              gutterBottom={true}
              title={t('tasks', 'subtasks')}
            />
            <LinearProgress
              customLabel={`${nDoneItems}/${nItems}`}
              label={true}
              value={(nDoneItems / nItems) * 100}
              variant="determinate"
            />
            <SimpleTable
              columns={[
                { align: 'left' },
                { align: 'right', width: '1%' },
                { align: 'right', width: '1%' },
                { align: 'right', width: '1%' },
                { align: 'right', width: '1%' }
              ]}
              emptyPlaceholder={t('tasks', 'empty_title')}
              hover={true}
              maxHeight={400}
              rows={items.map((item) => ({
                cells: [
                  <GenericListItem
                    disablePadding={true}
                    icon={{ name: 'Task' }}
                    key="title"
                    size="small"
                    sx={{ maxWidth: { xs: 256, lg: 400 } }}
                    title={item.title || t('global', 'unnamed')}
                  />,

                  <TaskStatusChip
                    key="status"
                    id={item.id}
                    size="xsmall"
                    value={item.status}
                  />,
                  <DeadlineField
                    key="deadline"
                    onChange={(value) => handleUpdateDeadline(item.id, value)}
                    status={item.status}
                    value={item.deadline_date}
                  />,
                  <SelectUser
                    key="owner"
                    onChange={(value) => handleUpdateUser(item.id, value)}
                    short={true}
                    size="small"
                    value={item.responsible_account_id}
                  />,
                  <OptionsMenuButton
                    key="options"
                    options={[
                      {
                        iconName: 'Edit',
                        onClick: () => setEditItem(item),
                        title: t('global', 'edit')
                      },
                      ...(item.deadline_date
                        ? [
                            {
                              iconName: 'ClearOutlined' as const,
                              onClick: () =>
                                handleUpdateDeadline(item.id, null),
                              title: t('tasks', 'remove_deadline')
                            }
                          ]
                        : []),
                      {
                        color: 'error',
                        iconName: 'Delete',
                        onClick: () => setDeleteItem(item),
                        title: t('global', 'delete')
                      }
                    ]}
                    size="small"
                  />
                ],
                id: item.id
              }))}
              size="small"
            />
          </>
        )}

        <AddSubtaskItem
          onClose={() => setAddSubtask(false)}
          open={addSubtask}
          parentId={parentId}
          sx={{ mt: 1 }}
        />
      </Box>

      {/*-- dialogs --*/}
      <EditSubtaskDialog
        item={editItem}
        onClose={() => setEditItem(null)}
        onSubmit={(body) => {
          if (editItem) {
            tryUpdateTask(editItem.id, body)
          }
        }}
      />

      <DeleteDialog
        onClose={() => setDeleteItem(null)}
        onDelete={(cb) => {
          if (!deleteItem) return

          tryDeleteTask(deleteItem.id, (err) => {
            if (!err) {
              setDeleteItem(null)
            }
            cb()
          })
        }}
        open={Boolean(deleteItem)}
        title={
          deleteItem ? deleteItem.title || t('global', 'unnamed') : undefined
        }
        type={t('tasks', 'subtask', true)}
      />
    </>
  )
}

export default SubtasksTable
